import React, { forwardRef, useRef, useEffect } from "react";
import JsBarcode from "jsbarcode";
import { User } from "lucide-react";
import { LOGO_URL, GOLD_STAR_3D, BAH_FLAG } from "@/lib/api";

// International tribal membership ID card sized to standard ID-1 proportions
// (1.586:1) with everything fitting: logo, title, flag, star, photo, details,
// compliance chips, and a scannable CODE128 barcode.
const MembershipIDCard = forwardRef(({ data, photoUrl }, ref) => {
  const barcodeRef = useRef(null);
  const photo = photoUrl || (data && data.photo_url);

  useEffect(() => {
    if (barcodeRef.current && data && data.credential_id) {
      try {
        JsBarcode(barcodeRef.current, data.credential_id, {
          format: "CODE128", displayValue: true, fontSize: 9, font: "monospace",
          textMargin: 0, height: 22, width: 1.0, margin: 2,
          background: "#ffffff", lineColor: "#0b0b0b",
        });
      } catch (e) { /* ignore */ }
    }
  }, [data]);

  if (!data) return null;

  return (
    <div
      ref={ref}
      data-testid="membership-id-card"
      className="relative w-full max-w-[480px] aspect-[1.586/1] bg-[#0b0b0b] border border-[#D4AF37]/45 overflow-hidden"
      style={{ boxShadow: "0 0 0 1px rgba(212,175,55,0.15), 0 30px 60px -20px rgba(0,0,0,0.9)" }}
    >
      <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: `url(${LOGO_URL})`, backgroundSize: "cover", backgroundPosition: "center" }} />
      <div className="absolute top-0 inset-x-0 h-px" style={{ background: "linear-gradient(90deg,transparent,rgba(212,175,55,0.7),transparent)" }} />

      <img src={GOLD_STAR_3D} alt="Compliance star" data-testid="id-gold-star" className="absolute top-1.5 right-1.5 w-7 h-7 object-contain mix-blend-lighten" crossOrigin="anonymous" />

      <div className="relative h-full p-3 flex flex-col justify-between">
        {/* Header */}
        <div className="flex items-center gap-2 pr-8">
          <img src={LOGO_URL} alt="7 Generations Indigenous Trust" data-testid="id-logo" className="h-7 w-auto object-contain shrink-0" crossOrigin="anonymous" />
          <div className="min-w-0">
            <p className="font-mono text-[6.5px] tracking-[0.16em] uppercase text-[#D4AF37] leading-tight truncate">Mississippian Heritage · Xulla Affiliate</p>
            <p className="font-serif text-[12px] text-[#E8D399] leading-tight">Multinational Tribal Identification Card</p>
          </div>
        </div>

        {/* Body */}
        <div className="flex items-center gap-2.5">
          <div className="relative w-[52px] shrink-0 border border-[#D4AF37]/40 bg-black/40 overflow-hidden flex items-center justify-center" style={{ aspectRatio: "3/4" }}>
            {photo ? (
              <img src={photo} alt="ID portrait" className="w-full h-full object-cover" crossOrigin="anonymous" />
            ) : (
              <User size={22} className="text-[#8A733F]" strokeWidth={1.2} />
            )}
            <img src={BAH_FLAG} alt="Black American Heritage Flag" data-testid="id-heritage-flag" className="absolute bottom-0 left-0 w-5 h-3 object-cover border-t border-r border-[#D4AF37]/40" crossOrigin="anonymous" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-mono text-[6px] tracking-[0.22em] uppercase text-[#8A733F]">Constituent</p>
            <p className="font-serif text-[13px] text-[#F5F0EB] truncate leading-tight">{data.holder_name}</p>
            <p className="font-mono text-[6.5px] tracking-[0.16em] uppercase text-[#A19D98] mb-1">{data.class}</p>
            <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
              <div>
                <p className="font-mono text-[6px] tracking-[0.18em] uppercase text-[#8A733F]">Credential</p>
                <p className="font-mono text-[9px] text-[#E8D399] truncate">{data.credential_id}</p>
              </div>
              <div>
                <p className="font-mono text-[6px] tracking-[0.18em] uppercase text-[#8A733F]">Date of Birth</p>
                <p className="font-mono text-[9px] text-[#E8D399] truncate">{data.dob || "—"}</p>
              </div>
              <div>
                <p className="font-mono text-[6px] tracking-[0.18em] uppercase text-[#8A733F]">Region</p>
                <p className="font-mono text-[9px] text-[#E8D399] truncate">{data.region || "—"}</p>
              </div>
              <div>
                <p className="font-mono text-[6px] tracking-[0.18em] uppercase text-[#8A733F]">Member Since</p>
                <p className="font-mono text-[9px] text-[#E8D399]">{data.member_since}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer: chips + barcode */}
        <div className="flex items-end justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1">
              {[data.valid_thru || "NON-EXPIRING", "UNDRIP"].map((chip) => (
                <span key={chip} className="font-mono text-[6px] tracking-[0.1em] uppercase text-[#0b0b0b] bg-[#D4AF37] px-1.5 py-0.5">{chip}</span>
              ))}
            </div>
            <p className="font-mono text-[6px] tracking-[0.06em] uppercase text-[#E8D399] mt-1 leading-snug">
              {data.undrip || "UN Declaration on the Rights of Indigenous Peoples · Compliant"}
            </p>
          </div>
          <div className="bg-white p-0.5 shrink-0 rounded-[2px]">
            <svg ref={barcodeRef} data-testid="id-barcode" className="block h-[30px] w-auto" />
          </div>
        </div>
      </div>
    </div>
  );
});

MembershipIDCard.displayName = "MembershipIDCard";
export default MembershipIDCard;
